// Shared aliyah range parsing + metadata for a parasha.
// Used by ParashaOverviewPage and ChooseReadingsPage.

import tora from '../../data/torah.json';
import ashkenazAlyiot from '../../data/ashkenaz_alyiot.json';
import { Pasuk } from '../../data/psukim';

export type AliyahRange = {
  start: { chapter: number; pasuk: number };
  end: { chapter: number; pasuk: number };
};

export function parseAliyahRange(range: string): AliyahRange | null {
  const parts = range.split('-');
  if (parts.length < 2) return null;
  const startStr = parts[0];
  const endStr = parts.slice(1).join('-');
  const [sc, sp] = startStr.split(':').map(Number);
  const [ec, ep] = endStr.split(':').map(Number);
  if (isNaN(sc) || isNaN(sp) || isNaN(ec) || isNaN(ep)) return null;
  return { start: { chapter: sc, pasuk: sp }, end: { chapter: ec, pasuk: ep } };
}

function cmpVerse(a: { chapter: number; pasuk: number }, b: { chapter: number; pasuk: number }) {
  return a.chapter !== b.chapter ? a.chapter - b.chapter : a.pasuk - b.pasuk;
}

export function isInRange(verse: Pasuk, range: AliyahRange): boolean {
  return cmpVerse(verse, range.start) >= 0 && cmpVerse(verse, range.end) <= 0;
}

export const ALIYAH_LABELS_HE = [
  'עלייה ראשונה',
  'עלייה שנייה',
  'עלייה שלישית',
  'עלייה רביעית',
  'עלייה חמישית',
  'עלייה שישית',
  'מפטיר',
];
export const ALIYAH_LABELS_EN = [
  'First Aliyah',
  'Second Aliyah',
  'Third Aliyah',
  'Fourth Aliyah',
  'Fifth Aliyah',
  'Sixth Aliyah',
  'Maftir',
];

export function getAliyahLabel(aliyahIdx: number, isHebrew: boolean): string {
  return isHebrew
    ? (ALIYAH_LABELS_HE[aliyahIdx] ?? `עלייה ${aliyahIdx + 1}`)
    : (ALIYAH_LABELS_EN[aliyahIdx] ?? `Aliyah ${aliyahIdx + 1}`);
}

export function getParashaVerses(parashaKey: string): Pasuk[] {
  return (tora as Pasuk[]).filter(
    (v) =>
      v.parasha &&
      v.parasha.toLowerCase().replace(/'/g, '') === parashaKey.toLowerCase().replace(/'/g, ''),
  );
}

export interface AliyahMeta {
  label: string;
  /** 1-based verse numbers within the parasha */
  verseStart: number;
  verseEnd: number;
  /** 0-based indices of the aliyah's verses within the parasha verse list */
  globalIndices: number[];
  isMaftir: boolean;
}

export function getAliyahMetas(parashaKey: string, isHebrew: boolean): AliyahMeta[] {
  const rawRanges = (ashkenazAlyiot as Record<string, string[]>)[parashaKey] || [];
  if (!rawRanges.length) return [];
  const parashaVerses = getParashaVerses(parashaKey);

  return rawRanges.map((rangeStr, aliyahIdx) => {
    const range = parseAliyahRange(rangeStr);
    const versesInAliyah = parashaVerses
      .map((v, globalIdx) => ({ verse: v, globalIdx }))
      .filter(({ verse }) => range && isInRange(verse, range));

    const verseStart = versesInAliyah.length > 0 ? versesInAliyah[0].globalIdx + 1 : 0;
    const verseEnd =
      versesInAliyah.length > 0 ? versesInAliyah[versesInAliyah.length - 1].globalIdx + 1 : 0;
    const globalIndices = versesInAliyah.map((v) => v.globalIdx);
    const isMaftir = aliyahIdx === rawRanges.length - 1;

    return {
      label: getAliyahLabel(aliyahIdx, isHebrew),
      verseStart,
      verseEnd,
      globalIndices,
      isMaftir,
    };
  });
}
